"use client";

import VERTICAL_OPTIONS from "@/constants/VerticalOptions";
import { Box, styled, Typography } from "@mui/material";
import FacebookOutlinedIcon from "@mui/icons-material/FacebookOutlined";
import { BRAND_COLOR, FOOTER_GREY, WEB_URL_BASE } from "./LayoutConstants";

const BUYER_LINKS = [
  {
    text: "Home",
    url: "/",
  },
  {
    text: "Search",
    url: "/for-sale/everything-equine",
  },
  {
    text: "Buying Tips",
    url: WEB_URL_BASE + "/buying",
  },
];

const SELLER_LINKS = [
  {
    text: "Start Selling",
    url: "/sell",
  },
  {
    text: "Manage Ad",
    url: "/sell/dashboard",
  },
  {
    text: "Pricing",
    url: "/sell/pricing",
  },
];

const OTHERS_LINKS = [
  {
    text: "Buyer Guides",
    url: "/buyer-guides",
  },
  {
    text: "Insurance",
    url: "/insurance",
  },
  {
    text: "Our Services",
    url: WEB_URL_BASE + "/services",
  },
  {
    text: "Business Advertising",
    url: WEB_URL_BASE + "/advertise",
  },
  {
    text: "Help",
    url: WEB_URL_BASE + "/help",
  },
  {
    text: "Clubs",
    url: WEB_URL_BASE + "/clubs",
  },
  {
    text: "News",
    url: WEB_URL_BASE + "/news",
  },
];

const ABOUT_LINKS = [
  {
    text: "About Us",
    url: WEB_URL_BASE + "/about",
  },
  {
    text: "Contact Us",
    url: "/contact",
  },
  {
    text: "Testimonials",
    url: WEB_URL_BASE + "/testimonials",
  },
];

const Container = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    backgroundColor: `grey.${FOOTER_GREY}`,
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    borderTop: theme.spacing(0.5),
    borderTopStyle: "solid",
    borderColor: BRAND_COLOR,
  })
);

const LinksContainer = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    display: "flex",
    padding: theme.spacing(5, 10),
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 5,
    a: {
      ...theme.typography.body1,
      "&:hover": {
        textDecoration: "underline",
      },
    },
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(5, 5),
    },
    [theme.breakpoints.only("xs")]: {
      flexDirection: "column",
    },
  })
);

const LinkSection = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    display: "flex",
    flexDirection: "column",
    maxWidth: "300px",
    gap: 1,
    "> :first-child": {
      marginBottom: 1,
    },
  })
);

const BottomRibbon = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    display: "flex",
    backgroundColor: "grey.700",
    color: "white",
    padding: 2,
    gap: 2,
    ".MuiTypography-root": {
      ...theme.typography.body2,
    },
    a: {
      textDecoration: "underline",
    },
    alignItems: "center",
    "> :last-child": {
      marginLeft: "auto",
    },
    [theme.breakpoints.only("xs")]: {
      flexDirection: "column",
      gap: 2,
      alignItems: "center",
      "> :last-child": {
        marginLeft: "initial",
      },
    },
  })
);

export default function GlobalFooter() {
  return (
    <Container>
      <LinksContainer>
        <LinkSection>
          <Typography variant="h5" fontWeight="bold">
            Categories
          </Typography>
          {VERTICAL_OPTIONS.map((vertical) => (
            <a key={vertical.slug} href={"/for-sale/" + vertical.slug}>
              {vertical.label}
            </a>
          ))}
        </LinkSection>
        <LinkSection>
          <Typography variant="h5" fontWeight="bold">
            Buyers
          </Typography>
          {BUYER_LINKS.map((link) => (
            <a key={link.url} href={link.url}>
              {link.text}
            </a>
          ))}
        </LinkSection>
        <LinkSection>
          <Typography variant="h5" fontWeight="bold">
            Sellers
          </Typography>
          {SELLER_LINKS.map((link) => (
            <a key={link.url} href={link.url}>
              {link.text}
            </a>
          ))}
        </LinkSection>
        <LinkSection>
          <Typography variant="h5" fontWeight="bold">
            More
          </Typography>
          {OTHERS_LINKS.map((link) => (
            <a key={link.url} href={link.url}>
              {link.text}
            </a>
          ))}
        </LinkSection>
        <LinkSection>
          <Typography variant="h5" fontWeight="bold">
            About Horsezone
          </Typography>
          {ABOUT_LINKS.map((link) => (
            <a key={link.url} href={link.url}>
              {link.text}
            </a>
          ))}
          <Typography variant="body2" sx={{ marginTop: 2 }}>
            Use of this Web site constitutes acceptance of the{" "}
            <a href={WEB_URL_BASE + "/index.php?a=28&b=140"}>
              <Typography variant="body2" component="span">
                User Agreement
              </Typography>
            </a>{" "}
            and{" "}
            <a href={WEB_URL_BASE + "/privacy"}>
              <Typography variant="body2" component="span">
                Privacy Policy
              </Typography>
            </a>
            .
          </Typography>
          <Typography variant="body2">
            Designated trademarks and brands are the property of their
            respective owners.
          </Typography>
        </LinkSection>
      </LinksContainer>
      <BottomRibbon>
        <Typography>
          © {new Date().getFullYear()} Horsezone. All rights reserved.
        </Typography>
        <a href={WEB_URL_BASE + "/index.php?a=28&b=140"}>
          <Typography variant="body2">User Agreement</Typography>
        </a>
        <a href={WEB_URL_BASE + "/privacy"}>
          <Typography variant="body2">Privacy Policy</Typography>
        </a>
        <a href="https://www.facebook.com/horsezone/">
          <FacebookOutlinedIcon sx={{ fontSize: "30px" }} />
        </a>
      </BottomRibbon>
    </Container>
  );
}
